import { useEffect, useRef, useState, type DragEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { PurchaseImportResult, PurchaseInvoice } from '@kontor/shared';
import { apiUpload, ApiError } from '../../api';
import './purchase.css';

const ACCEPT = '.pdf,.xml,.jpg,.jpeg,.png,.webp,.heic,application/pdf,application/xml,text/xml,image/jpeg,image/png,image/webp,image/heic';

/** Der Browser kennt für XML und HEIC nicht immer den Typ. Der Server erkennt die Datei am Inhalt, hier darf nur kein falscher Typ den Upload abweisen. */
function plain(file: File): File {
  const ok = file.type === 'application/pdf' || file.type.startsWith('image/') || file.type === 'application/xml' || file.type === 'text/xml';
  return ok ? file : new File([file], file.name, { type: 'application/octet-stream' });
}

const errorText = (e: unknown): string => (e instanceof ApiError ? e.message : 'Die Datei konnte nicht übertragen werden.');

/**
 * Eigene Kamera in der Seite (nur mit https). Sie ersetzt die Kamera-App des Handys, deren Rückgabe an den Browser nicht überall zuverlässig ankommt. Ein Tipp auf
 * „Aufnehmen“ macht ein Foto der Rückkamera und gibt es als Datei weiter. Geht die Kamera nicht (keine Erlaubnis, kein https), steht der Grund da, und die
 * Schaltfläche „Datei wählen“ bleibt der Weg.
 */
function CameraDialog({ onFile, onClose }: { onFile: (file: File) => void; onClose: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let stopped = false;
    async function start() {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('Dieser Browser kann die Kamera nicht aus der Seite öffnen. Machen Sie das Foto mit der Kamera-App und wählen Sie es mit „Datei wählen“ aus.');
        return;
      }
      try {
        const media = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 2560 }, height: { ideal: 1920 } }, audio: false });
        if (stopped) return media.getTracks().forEach((t) => t.stop());
        stream.current = media;
        if (video.current) {
          video.current.srcObject = media;
          await video.current.play().catch(() => undefined);
        }
        setReady(true);
      } catch (e) {
        const denied = e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'SecurityError');
        setError(denied ? 'Der Browser darf die Kamera nicht benutzen. Erlauben Sie den Zugriff in den Seiteneinstellungen des Browsers und versuchen Sie es noch einmal.' : 'Die Kamera lässt sich nicht öffnen. Machen Sie das Foto mit der Kamera-App und wählen Sie es mit „Datei wählen“ aus.');
      }
    }
    void start();
    return () => {
      stopped = true;
      stream.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  function snap() {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext('2d')?.drawImage(v, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return setError('Das Foto konnte nicht erstellt werden. Versuchen Sie es noch einmal.');
        const stamp = new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Berlin' }).replace(/[^0-9]/g, '').slice(0, 14);
        stream.current?.getTracks().forEach((t) => t.stop());
        onFile(new File([blob], `Foto-${stamp}.jpg`, { type: 'image/jpeg' }));
        onClose();
      },
      'image/jpeg',
      0.92,
    );
  }

  return (
    <div className="camera" role="dialog" aria-modal="true" aria-label="Foto aufnehmen">
      <div className="camera__box">
        {error ? <div className="alert alert--error" role="alert">{error}</div> : <video ref={video} className="camera__video" playsInline muted />}
        {!error && !ready && <p className="camera__status" role="status">Kamera wird geöffnet …</p>}
        <div className="camera__actions">
          <button type="button" className="btn btn--primary" disabled={!ready} onClick={snap}>Aufnehmen</button>
          <button type="button" className="btn btn--secondary" onClick={onClose}>{error ? 'Schließen' : 'Abbrechen'}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * „Import“ ganz oben in der Maske „Eingangsrechnung erstellen“ (KON-142). Datei wählen oder hierher ziehen, auf dem Handy auch „Foto aufnehmen“. Eine E-Rechnung
 * (ZUGFeRD, Factur-X, XRechnung) liest Wirkia aus, alles andere hängt es als Beleg an, und man erfasst die Felder von Hand. Beides legt einen Entwurf an.
 */
export function PurchaseImportCard({ onImported }: { onImported: (result: PurchaseImportResult) => void }) {
  const files = useRef<HTMLInputElement>(null);
  const [shooting, setShooting] = useState(false);
  const [over, setOver] = useState(false);
  const send = useMutation({
    mutationFn: async (file: File) => (await apiUpload<{ data: PurchaseImportResult }>('/invoices-in/import', plain(file))).data,
    onSuccess: onImported,
  });
  const pick = (list: FileList | null) => {
    const file = list?.[0];
    if (file) send.mutate(file);
  };
  const drop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    pick(e.dataTransfer.files);
  };

  return (
    <section className="card import" aria-labelledby="import-title">
      <h2 id="import-title">Import</h2>
      <p className="muted small">Eine E-Rechnung (ZUGFeRD-PDF, Factur-X, XRechnung) liest Wirkia aus und füllt die Felder. Jede andere PDF oder ein Foto hängt Wirkia als Beleg an, dann erfassen Sie die Felder von Hand. Ohne Datei können Sie auch alles unten eintragen.</p>
      <div
        className={`import__drop${over ? ' is-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
      >
        <p className="import__hint">Datei hierher ziehen oder</p>
        <div className="import__buttons">
          <button type="button" className="btn btn--primary" disabled={send.isPending} onClick={() => files.current?.click()}>Datei wählen</button>
          <button type="button" className="btn btn--secondary import__camera" disabled={send.isPending} onClick={() => setShooting(true)}>Foto aufnehmen</button>
        </div>
        <p className="muted small">PDF, JPG, PNG, WEBP, HEIC oder XML, höchstens 20 MB</p>
        <input ref={files} type="file" accept={ACCEPT} hidden onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
      </div>
      {shooting && <CameraDialog onFile={(file) => send.mutate(file)} onClose={() => setShooting(false)} />}
      {send.isPending && <p role="status">Die Datei wird gelesen …</p>}
      {send.isError && <div className="alert alert--error" role="alert">{errorText(send.error)}</div>}
    </section>
  );
}

/** Eine weitere Seite oder Anlage an einen Entwurf hängen (zweites Foto, PDF aus der E-Mail) */
export function AddDocument({ invoiceId, onAdded }: { invoiceId: string; onAdded: (invoice: PurchaseInvoice) => void }) {
  const files = useRef<HTMLInputElement>(null);
  const [shooting, setShooting] = useState(false);
  const send = useMutation({
    mutationFn: async (file: File) => (await apiUpload<{ data: PurchaseInvoice }>(`/invoices-in/${invoiceId}/documents`, plain(file))).data,
    onSuccess: onAdded,
  });
  const pick = (list: FileList | null) => {
    const file = list?.[0];
    if (file) send.mutate(file);
  };
  return (
    <div className="import__add">
      <button type="button" className="btn btn--secondary btn--sm" disabled={send.isPending} onClick={() => files.current?.click()}>Beleg hinzufügen</button>
      <button type="button" className="btn btn--secondary btn--sm import__camera" disabled={send.isPending} onClick={() => setShooting(true)}>Foto aufnehmen</button>
      {shooting && <CameraDialog onFile={(file) => send.mutate(file)} onClose={() => setShooting(false)} />}
      <input ref={files} type="file" accept={ACCEPT} hidden onChange={(e) => { pick(e.target.files); e.target.value = ''; }} />
      {send.isPending && <span role="status" className="muted small">Wird übertragen …</span>}
      {send.isError && <span className="field__error" role="alert">{errorText(send.error)}</span>}
    </div>
  );
}
